<script setup lang="ts">
import type { ExportFormat, ImportEntry, JsonKeyConfig } from "@/types";

import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";

import {
  decodeFile,
  detectFormatFromFile,
  detectTxtFormat,
  getCsvHeaders,
  hasVoiscordHeader,
  parseCsvTsv,
  parseJson,
  validateEntry,
} from "@/composable/useDictionaryImport";

import { useDictionaryStore } from "@/stores/useDictionaryStore";
import { useSnackbarStore } from "@/stores/useSnackbarStore";
import { useVGuildStore } from "@/stores/useVGuildStore";

const props = defineProps<{
  modelValue: boolean;
  rawFile: File | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { mobile } = useDisplay();
const dictionaryStore = useDictionaryStore();
const vGuildStore = useVGuildStore();
const snackbarStore = useSnackbarStore();

// ─── フォーマット設定 ───────────────────────────────────────────
const format = ref<ExportFormat>("csv");
const hasHeader = ref(false);
const delimiter = ref<"," | "\t">(",");
const csvBeforeKey = ref("");
const csvAfterKey = ref("");
const jsonKeyConfig = ref<JsonKeyConfig>({ beforeKey: "before", afterKey: "after" });

const csvHeaders = computed(() => {
  if (!rawText.value || format.value === "json") return [];
  return getCsvHeaders(rawText.value, delimiter.value, hasHeader.value);
});

// ─── パース結果 ───────────────────────────────────────────────
const entries = ref<ImportEntry[]>([]);
const parseError = ref<string | null>(null);
const loading = ref(false);
const importing = ref(false);
// ─── ファイルリード ────────────────────────────────────────────
const rawText = ref("");
// ─── バリデーション済みエントリ ──────────────────────────────────
const validatedEntries = computed(() => entries.value.map(validateEntry));
const invalidCount = computed(() => validatedEntries.value.filter((e) => !e.valid).length);

// ─── チェックボックス選択 ────────────────────────────────────────
const selectedIndices = ref<Set<number>>(new Set());

watch(entries, () => {
  selectedIndices.value = new Set(validatedEntries.value.flatMap((e, i) => (e.valid ? [i] : [])));
});

const allValidCount = computed(() => validatedEntries.value.filter((e) => e.valid).length);
const headerChecked = computed(
  () => allValidCount.value > 0 && selectedIndices.value.size === allValidCount.value,
);
const headerIndeterminate = computed(
  () => selectedIndices.value.size > 0 && selectedIndices.value.size < allValidCount.value,
);

const toggleHeader = () => {
  if (headerChecked.value) {
    selectedIndices.value = new Set();
  } else {
    selectedIndices.value = new Set(validatedEntries.value.flatMap((e, i) => (e.valid ? [i] : [])));
  }
};

const toggleRow = (index: number) => {
  const next = new Set(selectedIndices.value);
  if (next.has(index)) {
    next.delete(index);
  } else {
    next.add(index);
  }
  selectedIndices.value = next;
};

const validEntries = computed(() =>
  validatedEntries.value
    .filter((e, i) => e.valid && selectedIndices.value.has(i))
    .map(({ before, after }) => ({ before, after })),
);

// ─── 件数チェック ─────────────────────────────────────────────
const maxCount = computed(() => (vGuildStore.boostCount > 0 ? 300 : 50));
const existingBeforeSet = computed(
  () => new Set(dictionaryStore.dictionaries.map((d) => d.before)),
);
const selectedNewCount = computed(
  () => validEntries.value.filter((e) => !existingBeforeSet.value.has(e.before)).length,
);
const willExceedLimit = computed(
  () => existingBeforeSet.value.size + selectedNewCount.value > maxCount.value,
);

// ─── ファイルリード ─────────────────────────────────────────────
const reParse = () => {
  if (!rawText.value) return;
  parseError.value = null;
  if (format.value === "json") {
    const result = parseJson(rawText.value, jsonKeyConfig.value);
    entries.value = result.entries;
    parseError.value = result.error;
  } else {
    entries.value = parseCsvTsv(rawText.value, {
      delimiter: delimiter.value,
      hasHeader: hasHeader.value,
      beforeKey: csvBeforeKey.value,
      afterKey: csvAfterKey.value,
    });
  }
};

const resolveCsvKeyPair = (headers: string[], hasVoiscord: boolean) => {
  const fallbackBeforeKey = headers[0] ?? "";
  const fallbackAfterKey = headers[1] ?? headers[0] ?? "";

  if (!hasVoiscord) {
    return { beforeKey: fallbackBeforeKey, afterKey: fallbackAfterKey };
  }

  const beforeKey = headers.find((header) => header === "変換前");
  const afterKey = headers.find((header) => header === "変換後");

  return {
    beforeKey: beforeKey ?? fallbackBeforeKey,
    afterKey: afterKey ?? fallbackAfterKey,
  };
};

const loadFile = async (file: File) => {
  loading.value = true;
  parseError.value = null;
  entries.value = [];
  try {
    rawText.value = await decodeFile(file);
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "txt") {
      const detected = detectTxtFormat(rawText.value);
      if (detected.error) {
        parseError.value = detected.error;
        return;
      }
      format.value = detected.format;
      delimiter.value = detected.delimiter;
      hasHeader.value = detected.hasHeader;
      csvBeforeKey.value = detected.beforeKey;
      csvAfterKey.value = detected.afterKey;
      jsonKeyConfig.value = detected.jsonKeyConfig;
    } else {
      const detectedFormat = detectFormatFromFile(file);
      format.value = detectedFormat;
      if (detectedFormat === "tsv") {
        delimiter.value = "\t";
      } else {
        delimiter.value = ",";
      }
      hasHeader.value = hasVoiscordHeader(rawText.value, delimiter.value);
      if (detectedFormat !== "json") {
        const headers = getCsvHeaders(rawText.value, delimiter.value, hasHeader.value);
        const keyPair = resolveCsvKeyPair(headers, hasHeader.value);
        csvBeforeKey.value = keyPair.beforeKey;
        csvAfterKey.value = keyPair.afterKey;
      } else {
        const keys = jsonArrayKeys.value;
        if (keys.length >= 2) {
          jsonKeyConfig.value = { beforeKey: keys[0], afterKey: keys[1] };
        } else if (keys.length === 1) {
          jsonKeyConfig.value = { beforeKey: keys[0], afterKey: keys[0] };
        }
      }
    }
    reParse();
  } catch {
    parseError.value = "ファイルの読み込みに失敗しました";
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.rawFile,
  (file) => {
    if (file) loadFile(file);
  },
);

// CSV ヘッダー変化時にキーを自動リセット
watch(csvHeaders, (headers) => {
  csvBeforeKey.value = headers[0] ?? "";
  csvAfterKey.value = headers[1] ?? headers[0] ?? "";
});

// キー変更・フォーマット変更時に再パース
watch(
  [format, csvBeforeKey, csvAfterKey, jsonKeyConfig],
  () => {
    reParse();
  },
  { deep: true },
);

// ─── JSON キー候補 ───────────────────────────────────────────
const jsonArrayKeys = computed(() => {
  if (!rawText.value || format.value !== "json") return [];
  try {
    const parsed: unknown = JSON.parse(rawText.value);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    const first = parsed[0];
    if (first !== null && typeof first === "object" && !Array.isArray(first)) {
      return Object.keys(first as object);
    }
  } catch {
    // ignore
  }
  return [];
});

// ─── インポート実行 ──────────────────────────────────────────
const doImport = async () => {
  if (validEntries.value.length === 0) return;
  importing.value = true;
  try {
    const result = await dictionaryStore.importDictionary(validEntries.value);
    if (result) {
      snackbarStore.addTopRight({
        color: "success",
        text: `インポート完了: 新規 ${result.imported} 件 / 上書き ${result.updated} 件`,
        timeout: 3000,
      });
      emit("update:modelValue", false);
    }
  } finally {
    importing.value = false;
  }
};

const close = () => emit("update:modelValue", false);

defineExpose({ doImport, validEntries, dictionaryStore });
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    class="p-dictionary__import-dialog"
    :fullscreen="mobile"
    max-width="760"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="bg-background">
      <v-toolbar theme="dark">
        <v-btn
          icon
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>辞書インポート</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn
            color="secondary"
            :disabled="validEntries.length === 0 || willExceedLimit || importing"
            :loading="importing"
            @click="doImport"
          >
            確定
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-container>
        <v-progress-linear
          v-if="loading"
          indeterminate
        />

        <!-- フォーマット設定 -->
        <v-card
          class="mb-4"
          variant="outlined"
        >
          <v-card-title class="text-subtitle-2">フォーマット設定</v-card-title>
          <v-card-text>
            <div
              class="d-flex align-center mb-2"
              style="gap: 8px 12px"
            >
              <v-select
                v-model="format"
                :items="[
                  { title: 'CSV', value: 'csv' },
                  { title: 'TSV', value: 'tsv' },
                  { title: 'JSON', value: 'json' },
                ]"
                label="形式"
                density="compact"
                hide-details
                style="min-width: 120px; max-width: 150px"
              />
              <template v-if="format !== 'json'">
                <v-select
                  v-model="delimiter"
                  :items="[
                    { title: 'カンマ (,)', value: ',' },
                    { title: 'タブ (\\t)', value: '\t' },
                  ]"
                  label="区切り文字"
                  density="compact"
                  hide-details
                  style="min-width: 150px; max-width: 180px"
                />
              </template>
            </div>
            <div
              class="d-flex align-center"
              style="gap: 8px 12px"
            >
              <template v-if="format !== 'json'">
                <v-select
                  v-model="csvBeforeKey"
                  :items="csvHeaders"
                  label="単語列"
                  density="compact"
                  hide-details
                  style="min-width: 150px; max-width: 200px"
                />
                <v-select
                  v-model="csvAfterKey"
                  :items="csvHeaders"
                  label="読み列"
                  density="compact"
                  hide-details
                  style="min-width: 150px; max-width: 200px"
                />
              </template>
              <template v-else-if="jsonArrayKeys.length > 0">
                <v-select
                  v-model="jsonKeyConfig.beforeKey"
                  :items="jsonArrayKeys"
                  label="変換前キー"
                  density="compact"
                  hide-details
                  style="min-width: 150px; max-width: 200px"
                />
                <v-select
                  v-model="jsonKeyConfig.afterKey"
                  :items="jsonArrayKeys"
                  label="変換後キー"
                  density="compact"
                  hide-details
                  style="min-width: 150px; max-width: 200px"
                />
              </template>
            </div>
            <div
              v-if="format !== 'json'"
              class="d-flex align-center"
              style="gap: 8px 12px"
            >
              <v-switch
                v-model="hasHeader"
                label="1行目を無視"
                density="compact"
                color="blue-accent-2"
                hide-details
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- エラー・警告 -->
        <v-alert
          v-if="parseError"
          type="error"
          class="mb-3"
          density="compact"
        >
          {{ parseError }}
        </v-alert>
        <v-alert
          v-if="willExceedLimit"
          type="error"
          class="mb-3"
          density="compact"
        >
          登録件数が上限（{{ maxCount }} 件）を超えます（現在: {{ existingBeforeSet.size }} 件 /
          新規: {{ selectedNewCount }} 件）。 インポートを続行できません。
        </v-alert>
        <v-alert
          v-else-if="invalidCount > 0"
          type="warning"
          class="mb-3"
          density="compact"
        >
          {{ invalidCount }} 件のエントリにエラーがあります。エラー行はスキップされます。
        </v-alert>

        <!-- プレビューテーブル -->
        <div class="text-caption mb-1">
          プレビュー（{{ validatedEntries.length }} 件 / 選択: {{ validEntries.length }} 件）
        </div>
        <v-table
          class="bg-background"
          density="compact"
          fixed-header
          height="360"
        >
          <thead>
            <tr class="bg-surface-lighten-1">
              <th style="width: 40px">
                <v-checkbox
                  :model-value="headerChecked"
                  :indeterminate="headerIndeterminate"
                  density="compact"
                  hide-details
                  @click.prevent="toggleHeader"
                />
              </th>
              <th>変換前</th>
              <th>変換後</th>
              <th>状態</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(entry, i) in validatedEntries"
              :key="i"
              :class="{ 'text-error': !entry.valid }"
            >
              <td>
                <v-checkbox
                  :model-value="selectedIndices.has(i)"
                  :disabled="!entry.valid"
                  density="compact"
                  hide-details
                  @update:model-value="toggleRow(i)"
                />
              </td>
              <td>{{ entry.before }}</td>
              <td>{{ entry.after }}</td>
              <td>
                <span
                  v-if="!entry.valid"
                  class="text-error text-caption"
                  >{{ entry.error }}</span
                >
                <span
                  v-else-if="existingBeforeSet.has(entry.before)"
                  class="text-warning text-caption"
                  >上書き</span
                >
                <span
                  v-else
                  class="text-success text-caption"
                  >新規</span
                >
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-container>
    </v-card>
  </v-dialog>
</template>
